interface Props {
  values: number[]
  width?: number
  height?: number
  stroke?: string
  fill?: string
  forecast?: number[]
  forecastLow?: number[]
  forecastHigh?: number[]
}

/**
 * Tiny inline-SVG sparkline. Purely presentational — no state, no deps.
 *
 * ViewBox is fixed at 0 0 VB_W VB_H; the SVG stretches to its container via
 * preserveAspectRatio="none" so callers control final pixel size via CSS.
 * Values are normalised min->bottom / max->top with a 2px vertical padding.
 *
 * When forecast data is provided, the sparkline extends with a dashed line
 * for the 7-day forecast, a confidence band between forecastLow and forecastHigh,
 * and a small vertical divider at the history/forecast boundary.
 */
export function Sparkline({
  values,
  width,
  height = 24,
  stroke = "currentColor",
  fill,
  forecast,
  forecastLow,
  forecastHigh,
}: Props) {
  const hasForecast = forecast && forecast.length > 0
  const VB_W = hasForecast ? 130 : 100
  const VB_H = 30
  const PAD = 2

  if (!values || values.length === 0) {
    return (
      <svg
        width={width ?? "100%"}
        height={height}
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        preserveAspectRatio="none"
        aria-hidden="true"
      />
    )
  }

  // Combine all values to compute shared min/max for consistent scale
  const allValues = [
    ...values,
    ...(forecast ?? []),
    ...(forecastLow ?? []),
    ...(forecastHigh ?? []),
  ]
  const min = Math.min(...allValues)
  const max = Math.max(...allValues)
  const span = max - min || 1

  const historyCount = values.length
  const forecastCount = forecast?.length ?? 0
  const totalCount = historyCount + forecastCount

  // X positions: history occupies 0..historySection, forecast occupies the rest
  const historySection = hasForecast ? 100 : VB_W
  const forecastSection = hasForecast ? VB_W - historySection : 0

  const xForHistory = (i: number) =>
    historyCount === 1 ? historySection / 2 : (i / (historyCount - 1)) * historySection
  const xForForecast = (i: number) =>
    forecastCount <= 1
      ? historySection + forecastSection / 2
      : historySection + ((i + 1) / forecastCount) * forecastSection

  const yFor = (v: number) => {
    const t = (v - min) / span
    return VB_H - PAD - t * (VB_H - PAD * 2)
  }

  // History path
  const histPoints = values.map((v, i) => `${xForHistory(i).toFixed(2)},${yFor(v).toFixed(2)}`)
  const polyPoints = histPoints.join(" ")
  const baseline = VB_H - PAD
  const areaPath = `M0,${baseline} L${histPoints.join(" L")} L${historySection},${baseline} Z`

  const lastHistX = xForHistory(historyCount - 1)
  const lastHistY = yFor(values[historyCount - 1] ?? 0)
  const fillColor = fill ?? stroke
  const fillOpacity = fill ? 1 : 0.15

  // Forecast paths
  let forecastPolyline = ""
  let confidenceBandPath = ""
  let lastForecastX = 0
  let lastForecastY = 0

  if (hasForecast && forecast) {
    // Connect from last history point through forecast points
    const fcPoints: string[] = [`${lastHistX.toFixed(2)},${lastHistY.toFixed(2)}`]
    for (let i = 0; i < forecast.length; i++) {
      const x = xForForecast(i)
      const y = yFor(forecast[i]!)
      fcPoints.push(`${x.toFixed(2)},${y.toFixed(2)}`)
    }
    forecastPolyline = fcPoints.join(" ")
    lastForecastX = xForForecast(forecast.length - 1)
    lastForecastY = yFor(forecast[forecast.length - 1]!)

    // Confidence band polygon
    if (forecastLow && forecastHigh && forecastLow.length > 0 && forecastHigh.length > 0) {
      const topEdge: string[] = []
      const bottomEdge: string[] = []
      const bandLen = Math.min(forecastLow.length, forecastHigh.length)
      for (let i = 0; i < bandLen; i++) {
        const x = xForForecast(i)
        topEdge.push(`${x.toFixed(2)},${yFor(forecastHigh[i]!).toFixed(2)}`)
        bottomEdge.push(`${x.toFixed(2)},${yFor(forecastLow[i]!).toFixed(2)}`)
      }
      // Polygon: top edge left-to-right, then bottom edge right-to-left
      confidenceBandPath = `M${topEdge.join(" L")} L${bottomEdge.reverse().join(" L")} Z`
    }
  }

  return (
    <svg
      width={width ?? "100%"}
      height={height}
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      {/* History area fill */}
      <path d={areaPath} fill={fillColor} fillOpacity={fillOpacity} stroke="none" />
      {/* History solid line */}
      <polyline
        points={polyPoints}
        fill="none"
        stroke={stroke}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      {/* Current value dot */}
      <circle cx={lastHistX} cy={lastHistY} r={1.6} fill={stroke} vectorEffect="non-scaling-stroke" />

      {hasForecast && forecast && (
        <>
          {/* Vertical divider at history/forecast boundary */}
          <line
            x1={historySection}
            y1={PAD}
            x2={historySection}
            y2={VB_H - PAD}
            stroke={stroke}
            strokeWidth={0.5}
            strokeOpacity={0.3}
            strokeDasharray="1,1"
            vectorEffect="non-scaling-stroke"
          />
          {/* Confidence band */}
          {confidenceBandPath && (
            <path d={confidenceBandPath} fill={stroke} fillOpacity={0.08} stroke="none" />
          )}
          {/* Forecast dashed line */}
          <polyline
            points={forecastPolyline}
            fill="none"
            stroke={stroke}
            strokeWidth={1.2}
            strokeOpacity={0.55}
            strokeDasharray="3,2"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
          {/* Forecast end dot */}
          <circle
            cx={lastForecastX}
            cy={lastForecastY}
            r={1.4}
            fill={stroke}
            fillOpacity={0.55}
            vectorEffect="non-scaling-stroke"
          />
        </>
      )}
    </svg>
  )
}
